import { AfterViewInit, ChangeDetectorRef, Component, DoCheck, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, inject } from "@angular/core";
import { ControlContainer, NgModelGroup } from "@angular/forms";
import { KeyHandlerService, SecurityService } from "@tylertech/core-module";
import { AutocompleteComponent, AutocompleteFilterCallback } from "@tylertech/forge";
import { getLoggerFor, ILogger, LogInvocation, LogLevel } from "@tylertech/logging";
import { ODataFilterFactoryService } from "@tylertech/odata-module";
import { Utilities } from "@tylertech/shared-module";
import { ILookupDetailProjection, IMetadataAction, KeyValuePair, RegexFormattingOptions } from "@tylertech/tyler-api-types";
import { AugmentedModelObject, MetadataKey, TypeaheadPropertyMetadata, ValueChangedParms } from "../../../../interfaces";
import { AutoCompleteOption, CachedCodeLookupService, CellEditService, MetadataActionService, MetadataInputFormatterService, MetadataInputSelectService, MetadataInputTypeHelperService, MetadataPropertyService, MetadataService } from "../../../../services";
import { MetadataActionHelpers, MetadataKeyHelpers, ObjectHelpers } from "../../../../utilities";

@Component({
    selector: "metadata-input-typeahead",
    templateUrl: "./metadata-input-typeahead.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    standalone: false
})
export class MetadataInputTypeaheadComponent implements OnInit, AfterViewInit, OnChanges, DoCheck
{
    modelGroup = inject(NgModelGroup);
    private cdr = inject(ChangeDetectorRef);
    private readonly $odataFilterFactory = inject(ODataFilterFactoryService);
    private $metadataService = inject(MetadataService);
    private $metadataPropertyService = inject(MetadataPropertyService);
    private $cellEditService = inject(CellEditService);
    private readonly $metadataInputSelectService = inject(MetadataInputSelectService);
    private readonly $metadataInputTypeHelperService = inject(MetadataInputTypeHelperService);
    private readonly $metadataActionService = inject(MetadataActionService);
    private $cachedCodeLookupService = inject(CachedCodeLookupService);
    private readonly $keyHandlerService = inject(KeyHandlerService);
    private readonly $securityService = inject(SecurityService);
    private readonly $metadataInputFormatterService = inject(MetadataInputFormatterService);

    private readonly log: ILogger;

    // not really readonly, but we should treat it as immutable within this component.
    /**
     * It is assumed that value comes from the `KeyValue.Key` field for the list of
     * objects returned from `ICodeLookupService`.
     */
    @Input()
    readonly model: MetadataKey;

    @Input()
    metadataModel: AugmentedModelObject; // model should be a field on metadataModel, FYI

    @Input()
    parent: AugmentedModelObject; // metadataModel should be a field on parent, FYI

    @Input()
    propertyMetadata: TypeaheadPropertyMetadata;

    @Input()
    viewOnly: boolean;

    @Input()
    dense: boolean;

    @Input()
    compact: boolean;

    @Input()
    forCellEdit: boolean;

    @Input()
    error: string;

    @Input()
    isSelect: boolean;

    @Output()
    onValueChanged = new EventEmitter<ValueChangedParms>();

    @ViewChild("autocomplete") autocomplete: AutocompleteComponent;
    /**
     * Metadata's Recalculate on change functionality mutates the metadataModel in a way that will not trigger a binding change event,
     * so we need to have a way to do a simple check in `$doCheck` to determine if the `this.model` value was changed with out our notice.
     * This should only be set in `$onInit` and `onModelChanged`.
     */
    private unboundModel: MetadataKey;

    private _valueModel: string = "";

    isViewOnly: boolean;

    private isInitialized: boolean = false;

    disableForRecalculate: boolean = false;

    allowRecalculateSpinner: boolean = false;

    controlId: string;

    skip: number = 0;

    top: number = 50;

    itemCount: number = 0;

    currentOptions: MetadataKey[];

    private lookupPropertyValues: KeyValuePair<MetadataKey, string>[];

    private _density: string;

    private searchText: string;

    private keyValue: MetadataKey;

    constructor()
    {
        this.controlId = Utilities.generateRandomGuid();
        this.log = getLoggerFor("MetadataInputTypeaheadComponent",
            () => this.propertyMetadata
                ? `MetadataInputTypeaheadComponent(${this.propertyMetadata.Name})`
                : "MetadataInputTypeaheadComponent");
    }

    ngOnInit(): void
    {
        this.filterOnFocus = !this.forCellEdit;
        this.unboundModel = this.model;
        this._density = this.$metadataService.getInputDensity(this.compact);
        this.autocomplete = null;
    }

    ngAfterViewInit(): void
    {
        const self = this;
        if (this.isInitialized)
            return;
        this.isInitialized = true;

        this.setFilterCallback();
        if (this.hasModel)
            this.updatedSelectedItem(this.model)?.then(() => this.selectTextForCellEdit());
    }

    private selectTextForCellEdit(): void
    {
        if (!this.forCellEdit)
            return;
        requestAnimationFrame(() =>
        {
            const inputEl = document.getElementById(this.controlId) as HTMLInputElement;
            if (inputEl && document.activeElement === inputEl && inputEl.value)
                inputEl.setSelectionRange(0, inputEl.value.length);
        });
    }

    setFilterCallback(): void
    {
        const self = this;
        const filter: AutocompleteFilterCallback = (filterText, initialValue) =>
        {
            if (initialValue)
                return self.getItems(null, initialValue, false);
            if (document.activeElement?.id !== self.controlId)
              return Promise.resolve([]);
            return self.getItems(filterText, null, false);
        };
        self.autocomplete.filter = filter;
    }

    get hasModel(): boolean
    {
        return !MetadataKeyHelpers.isNullKey(this.model);
    }

    ngDoCheck(): void
    {
        if (this.propertyMetadata.$$forceReload && this.autocomplete)
            this.onModelChanged(this.model);

        this.isViewOnly = (this.viewOnly && !this.propertyMetadata.ViewOnlyEnabled) ||
            this.$metadataPropertyService.isViewOnly(this.propertyMetadata, this.metadataModel, this.parent) ||
            this.disableForRecalculate;

        if (this.isInitialized && this.isSelect && this.updateLookupPropertyValue())
        {
            this.resetLoadMore();
            this.autocomplete.filterText = null;
        }
    }

    ngOnChanges(_changes: SimpleChanges): void
    {
        if (!this.isInitialized)
        {
            return;
        }
        const changedKeys = Object.keys(_changes);
        for (const key of changedKeys)
        {
            const change = _changes[key];
            switch (key)
            {
                case "model":
                    this.onModelChanged(change.currentValue);
                    break;
            }
        }
    }

    get isInvalid(): boolean
    {
        return this.$metadataService.invalidInput(this.error, this.modelGroup?.control?.controls[this.propertyMetadata.Name]);
    }

    get Density(): string
    {
        return this._density;
    }

    get LabelPosition(): string
    {
        return this.forCellEdit ? 'none' : this.compact ? 'block-start' : 'inset';
    }

    private onModelChanged(value: MetadataKey): void
    {
        const self = this;
        self.unboundModel = value;
        if (MetadataKeyHelpers.isNullKey(value))
        {
            this.setValue = null;
            this.autocomplete.value = null;
            self.propertyMetadata.$$forceReload = false;
        }
        else if (!self.autocomplete.value || !self.areEqual(self.autocomplete.value, value) || self.propertyMetadata.$$forceReload)
        {
            if (this.propertyMetadata.$$forceReload)
            {
                self.$cachedCodeLookupService.clearCache(self.propertyMetadata.Lookup, null);
            }

            this.autocomplete.value = value;
            self.propertyMetadata.$$forceReload = false;
            self.updatedSelectedItem(value, true);
        }
    }

    get valueModel(): string
    {
        return this._valueModel;
    }

    set valueModel(value: string)
    {
        if (Utilities.IsNullOrUndefined(value) && this.model === "" && this.isRequired) this._valueModel = "";
        else this._valueModel = value;
    }

    get showSpinner(): boolean
    {
        const showSpinnerValue = this.$metadataPropertyService.isBeingRecalculated(this.propertyMetadata, this.metadataModel);
        if (!showSpinnerValue)
        {
            let setFocus: boolean;
            if (this.disableForRecalculate)
                setFocus = true;
            this.disableForRecalculate = false;
            this.allowRecalculateSpinner = false;
            if (setFocus)
                setTimeout(() => document.getElementById(this.controlId)?.focus(), 50);

        }
        return this.allowRecalculateSpinner && showSpinnerValue;
    }

    private updateLookupPropertyValue(): boolean
    {
        if (!this.propertyMetadata.LookupProperty)
            return false;
        const list = this.$metadataService.DependentModuleValue(
            this.propertyMetadata.LookupProperty,
            this.parent,
            this.metadataModel
        ) ?? [];
        if ((Utilities.IsNullOrUndefined(list) && Utilities.IsNullOrUndefined(this.lookupPropertyValues)) ||
            ObjectHelpers.stringifyCircular(this.lookupPropertyValues) !== ObjectHelpers.stringifyCircular(list))
        {
            this.lookupPropertyValues = JSON.parse(ObjectHelpers.stringifyCircular(list));
            if (this.lookupPropertyValues.find(x => x.Key === this.model))
                return false;
            return true;
        }
        return false;
    }

    filterOnFocus: boolean = false;

    onFocus(): void
    {
        if (this.forCellEdit)
        {
            setTimeout(() =>
            {
                this.filterOnFocus = true;
                if (!this.isViewOnly && !this.autocomplete.open)
                    this.autocomplete.openDropdown();
            }, 100);
        }

        if (this.$metadataPropertyService.isBeingRecalculated(this.propertyMetadata, this.metadataModel))
        {
            this.disableForRecalculate = true;
            this.allowRecalculateSpinner = true;
        }
    }

    onBlur(): void
    {
        if (this.forCellEdit)
            this.filterOnFocus = false;
    }

    get hasModelDependentFilter(): boolean
    {
        return (
            (this.DependentUpon && this.DependentUpon.length > 0) ||
            (this.FilterBy && this.FilterBy.length > 0) ||
            this.HasDynamicFilter
        );
    }

    private get DependentUpon(): string[]
    {
        return this.propertyMetadata.DependentUpon ? this.propertyMetadata.DependentUpon.split(",") : null;
    }

    private get FilterBy(): string[]
    {
        return this.propertyMetadata.FilterBy ? this.propertyMetadata.FilterBy.split(",") : null;
    }

    private get HasDynamicFilter(): boolean
    {
        if (!this.propertyMetadata.ConstantFilter) return false;
        return this.propertyMetadata.ConstantFilter.includes("{") && this.propertyMetadata.ConstantFilter.includes("}");
    }

    get isRequired(): boolean
    {
        return (
            this.$metadataPropertyService.isRequired(this.propertyMetadata, this.metadataModel, this.parent) &&
            !this.isViewOnly
        );
    }

    get allowClear(): boolean
    {
        return !this.isViewOnly && !this.$metadataPropertyService.isBeingRecalculated(this.propertyMetadata, this.metadataModel) && !Utilities.IsNullOrUndefined(this.model);
    }

    get allowDetail(): boolean
    {
        return this.propertyMetadata.AllowLookupDetail && !this.isViewOnly && !this.$metadataPropertyService.isBeingRecalculated(this.propertyMetadata, this.metadataModel);
    }

    get Name(): string
    {
        return this.propertyMetadata.Name;
    }

    get Label(): string
    {
        return this.forCellEdit
            ? " "
            : this.$metadataService.EvaluateString(this.propertyMetadata.Label, this.parent, this.metadataModel);
    }

    get MaxLength(): number
    {
        return this.propertyMetadata.FreeEntry
            ? this.$metadataInputTypeHelperService.getMaxLength(this.propertyMetadata, this.metadataModel, this.parent)
            : undefined;
    }

    get Regex(): string
    {
        return this.$metadataPropertyService.getRegex(this.propertyMetadata, this.metadataModel, this.parent);
    }

    get RegexOptions(): RegexFormattingOptions
    {
        return this.$metadataPropertyService.getRegexFormatter(this.propertyMetadata, this.metadataModel, this.parent);
    }

    get hasHint(): boolean
    {
        return !!this.propertyMetadata && !!this.Hint;
    }

    get Hint(): string
    {
        return this.propertyMetadata.Hint
            ? this.propertyMetadata.Hint
            : !this.propertyMetadata.HintProperty
                ? null
                : this.$metadataService.DependentModuleValue(
                    this.propertyMetadata.HintProperty,
                    this.parent,
                    this.metadataModel
                );
    }

    clear(): void
    {
        this.resetLoadMore();
        this.setValue = undefined;
        this.valueModel = null;
        this.autocomplete.filterText = null;
        this.onSearchTextChanged(null);
        requestAnimationFrame(() => this.autocomplete.openDropdown());
    }

    onSearchTextChanged($event: any): void
    {
        if ($event?.target && !$event.target.value && this.autocomplete.filterText)
            this.autocomplete.filterText = "";
        if ((!this.valueModel || this.valueModel === ""))
            this.fireValueChanged(null);
    }

    onChange(): void
    {
        const doWeHaveSearchText = !Utilities.IsNullOrUndefinedOrEmpty(this.autocomplete.filterText);
        if (!doWeHaveSearchText)
        {
            return;
        }
        if (this.propertyMetadata.FreeEntry)
        {
            if (this.setValue)
            {
                this.valueModel = this.setValue;
                this.setValue = undefined;
            }
            this.$cellEditService.FreeEntryValue = this.forCellEdit && !this.setValue;
            this.onSelectedItemChange({ detail: this.valueModel });
            this.updatedSelectedItem(this.valueModel);
            return;
        }
        if (!MetadataKeyHelpers.isNullKey(this.model))
        {
            this.updatedSelectedItem(this.model);
            return;
        }
        this.resetLoadMore();
        this.valueModel = null;
        this.autocomplete.filterText = null;
        this.fireValueChanged(null);
    }

    setValue: any;

    onSelectedItemChange($event: any): void
    {
        if (this.$metadataPropertyService.isBeingRecalculated(this.propertyMetadata, this.metadataModel))
            return;
        let detail = $event.detail;
        if (this.propertyMetadata.FreeEntry)
        {
            if (this.RegexOptions && this.RegexOptions.BackEndValueRegexReplace)
                detail = this.$metadataInputFormatterService.GetFormattedModel(detail, this.RegexOptions);
            this.setValue = detail;
        }
        if (!Utilities.IsNullOrUndefined(detail))
        {
            if (!this.areEqual(this.unboundModel, detail))
            {
                this.fireValueChanged(detail);
            }
        }
        else if (!this.areEqual(this.unboundModel, detail) && this.hasModelDependentFilter && !this.propertyMetadata.FreeEntry)
        {
            setTimeout(() => this.fireValueChanged(null), 100);
        }
    }

    fireValueChanged(value: MetadataKey): void
    {
        if (!this.checkEquality(value))
            return;
        this.valueModel = this.autocomplete.filterText;
        if (value === null && this.isSelect && this.isRequired)
            this.propertyMetadata["$$skipRecalculate"] = true;
        this.onValueChanged.emit({ value: value, cd: true });
    }

    checkEquality(value: MetadataKey): boolean
    {
        let old = this.model;
        if (typeof value === "string" && !Utilities.IsNullOrUndefined(old))
            old = old.toString();

        return !this.areEqual(old, value);
    }

    areEqual(oldItem: MetadataKey, newItem: MetadataKey): boolean
    {
        if (MetadataKeyHelpers.isNullKey(oldItem) && MetadataKeyHelpers.isNullKey(newItem))
            return true;
        return oldItem === newItem;
    }

    @LogInvocation(LogLevel.DEBUG)
    private updatedSelectedItem(value: MetadataKey, fromForceReload: boolean = false): Promise<any>
    {
        if (this.$metadataPropertyService.AreAnyControllingPropertiesMissingValues(
            this.propertyMetadata,
            this.metadataModel,
            this.parent
        ))
        {
            return;
        }

        if (MetadataKeyHelpers.isNullKey(value))
        {
            return;
        }
        const criteria = this.createOdataFilter(null, value, this.isSelect ? 0 : this.top, this.skip);
        return this.getItemsImpl(this.propertyMetadata.Lookup, criteria, false, true)
            .then(
                (convertedResults: AutoCompleteOption[]) =>
                {
                    if (!MetadataKeyHelpers.isNullKey(this.model))
                    {
                        const match = convertedResults.find(x => x.value.toString() === this.model.toString());
                        if (match)
                        {
                            this.autocomplete.value = match.value;
                            this.valueModel = match.label;
                            return;
                        }
                    }

                    if (convertedResults.length > 0)
                    {
                        if (MetadataKeyHelpers.isNumberKey(this.model) && this.model === 0)
                        {
                            const match = convertedResults.find(x => x.value.toString() === this.model.toString());
                            if (match)
                            {
                                this.autocomplete.value = match.value;
                                this.valueModel = match.label;
                            }
                            return;
                        }
                        this.autocomplete.value = convertedResults[0].value;
                        this.valueModel = convertedResults[0].label;
                        return;
                    }

                    if (this.propertyMetadata.FreeEntry)
                    {
                        this.autocomplete.value = value;
                        this.valueModel = value.toString();
                        return;
                    }

                    this.log.warning(`updatedSelectedItem|No KeyValue was found corresponding to ${JSON.stringify(value)}!`);
                    this.autocomplete.value = null;
                },
                (error) =>
                {
                    this.log.error(error);
                })
                .finally(() =>
                {
                    if (fromForceReload)
                    {
                        this.autocomplete.forceFilter();
                        this.cdr.detectChanges();
                    }
                });
    }

    resetLoadMore(): void
    {
        this.skip = 0;
        this.top = 50;
    }

    loadMore(_$event: any): void
    {
        const self = this;
        //TODO: eventually use skip properly instead of extending top when all lookups can handle it
        //this.skip += 20;
        if (this.isSelect || this.top >= 500 || this.itemCount < this.top)
            return;
        this.top += 50;
        this.getItems(this.autocomplete.filterText, null, true).then((results) =>
        {
            self.autocomplete.appendOptions(results);
        });
    }

    getItems(searchText: string, keyValue: MetadataKey, loadMore: boolean): Promise<AutoCompleteOption[]>
    {
        const self = this;
        const lookupType = this.propertyMetadata.Lookup;
        const filterString = self.createOdataFilter(searchText, keyValue, this.isSelect ? 0 : this.top, this.skip);
        if (Utilities.IsNullOrUndefined(filterString)) return Promise.resolve([]);
        return self.getItemsImpl(lookupType, filterString, loadMore, false)
            .then(x => x, onError);
        function onError(error): Array<any>
        {
            console.error(
                `${MetadataInputTypeaheadComponent.name}.getItems|lookupType: ${JSON.stringify(
                    lookupType
                )}|filter: ${JSON.stringify(filterString)}`,
                error
            );
            return [];
        }
    }

    private getItemsImpl(lookupType: string, filterString: string, loadMore: boolean, loadSelected: boolean): Promise<AutoCompleteOption[]>
    {
        const self = this;

        const lookupPromise = lookupType
            ? this.$cachedCodeLookupService.Get(lookupType, filterString)
            : Promise.resolve([]);

        const lookupPropertyPromise = this.propertyMetadata.LookupProperty
            ? Promise.resolve(this.$metadataService.DependentModuleValue(
                this.propertyMetadata.LookupProperty,
                this.parent,
                this.metadataModel))
            : Promise.resolve([]);

        return Promise.all([lookupPromise, lookupPropertyPromise])
            .then(([lookupResults, propertyResults]) =>
            {
                const merged = [...(lookupResults || []), ...(propertyResults || [])];
                if (this.isSelect)
                    return localFilter(merged);
                return convert(merged);
            });

        function localFilter(result: KeyValuePair<MetadataKey, string>[]): AutoCompleteOption[]
        {
            if (self.keyValue)
                result = result.filter(x => x.Key?.toString() === self.keyValue.toString());
            else if (self.searchText)
                result = result.filter(x => x.Value.toLowerCase().indexOf(self.searchText.toLowerCase()) > -1);
            self.keyValue = undefined;
            self.searchText = undefined;
            return convert(result);
        }

        function convert(result: KeyValuePair<MetadataKey, string>[]): AutoCompleteOption[]
        {
            result = result.filter((item, index, self) => index === self.findIndex(x => x.Key === item.Key && x.Value === item.Value));
            if (!loadSelected)
            {
                if (Utilities.IsNullOrUndefined(result))
                    result = [];
                self.itemCount = result.length;
            }
            return self.convertToAutoCompleteOptions(result, loadMore);
        }
    }

    private convertToAutoCompleteOptions(x: KeyValuePair<MetadataKey, string>[], loadMore: boolean): AutoCompleteOption[]
    {
        let newArray: AutoCompleteOption[] = [];
        x.forEach(element =>
        {
            if (element.Value)
                element.Value = element.Value.replace(/\r\n/g, " ");
            else
                element.Value = "";


            const temp: AutoCompleteOption = {
                value: element.Key,
                label: element.Value
            };
            newArray.push(temp);
        });
        newArray = this.$metadataInputSelectService.MergeTypeAheadOptions(
            this.propertyMetadata.Lookup,
            newArray,
            this.propertyMetadata,
            this.metadataModel,
            this.parent,
            this.autocomplete.filterText
        );
        if (loadMore && !this.isSelect)
        {
            const onlyNewOptions = newArray.filter(x => this.currentOptions.indexOf(x.value) < 0);
            this.currentOptions = this.currentOptions.concat(...onlyNewOptions.map(x => x.value));
            return onlyNewOptions;
        }

        this.currentOptions = newArray.map(x => x.value);
        return newArray;
    }

    private createOdataFilter(searchText: string, keyValue: MetadataKey, top: number = 0, skip: number = 0): string
    {
        this.searchText = searchText;
        this.keyValue = keyValue;
        return this.$metadataPropertyService.createOdataFilter(
            this.propertyMetadata,
            this.metadataModel,
            this.parent,
            this.isSelect ? null : searchText,
            this.isSelect ? null : !Utilities.IsNullOrUndefined(keyValue) ? keyValue.toString() : null,
            top,
            skip
        );
    }

    enforceFormat(event): void
    {
        if (event.code === "NumpadAdd" && this.$securityService.UseTenKeyPlusAsTab)
        {
            event.preventDefault();
            this.$keyHandlerService.selectNextTabbableElement();
        }
        else if (this.RegexOptions && this.RegexOptions.ValidCharRegex && !this.$keyHandlerService.ignoreForFormatting(event))
        {
            const keyHit = event.key;
            if (keyHit.match(new RegExp(this.RegexOptions.ValidCharRegex)) == null)
            {
                event.preventDefault();
            }
        }
    }

    handleIconButtonKeyDown(event): void
    {
        if (event.code === "NumpadAdd" && this.$securityService.UseTenKeyPlusAsTab)
        {
            event.preventDefault();
            this.$keyHandlerService.selectNextTabbableElement();
        }
    }

    viewDetail(): void
    {
        const self = this;
        let action: IMetadataAction;
        this.$cachedCodeLookupService.GetLookupDetailProjection(this.propertyMetadata.Lookup)
            .then((response: ILookupDetailProjection) =>
            {
                action = response.Action;
                action.Label = this.$metadataService.EvaluateString(self.propertyMetadata.Label, this.parent, this.metadataModel);
                action.SeparateFormData = true;
                action.ViewToShow["$LookupDetail"] = true;
                action.SaveActionLabel = "Select";

                const searchFilter = self.createOdataFilter(null, null).replace("&$filter=", "");
                const originalConstant = action.ViewToShow.ConstantFilter;
                if (!Utilities.IsNullOrUndefinedOrEmpty(searchFilter))
                {
                    action.ViewToShow.ConstantFilter = searchFilter;
                    if (!Utilities.IsNullOrUndefinedOrEmpty(originalConstant))
                        action.ViewToShow.ConstantFilter += ` and ${originalConstant}`;
                }

                if (response.FiltersToIgnore)
                {
                    response.FiltersToIgnore.forEach(f =>
                    {
                        action.ViewToShow.ConstantFilter = self.$odataFilterFactory.removeFilterClause(f, action.ViewToShow.ConstantFilter);
                    });
                }
                const formDataOriginal = self.metadataModel["$FormData"];
                self.$metadataActionService.ExecuteAction(self.metadataModel, action, null)
                    .then((model) =>
                    {
                        self.metadataModel["$FormData"] = formDataOriginal;
                        if (MetadataActionHelpers.IsCancelObject(model))
                            return;
                        const newValue = action.ViewToShow["$LookupDetailId"];
                        delete action.ViewToShow["$LookupDetailId"];
                        const changed = this.checkEquality(newValue);
                        if (changed)
                        {
                            self.valueModel = newValue;
                            self.fireValueChanged(newValue);
                        }
                    });
            });
    }
}
